import { useState, useRef } from "react";
import { useClickOutside } from "./useClickOutside";

export type QROptions = {
  withLogo: boolean;
  selectedLogoId?: string;
};

interface UseURLEntryProps {
  name: string;
  url: string;
  expirationDate: string;
  defaultExpanded?: boolean;
  customColor?: string;
  withLogo?: boolean;
  prefix: string;
  logoId?: string;
}

export const useURLEntry = ({
  name,
  url,
  expirationDate,
  defaultExpanded = false,
  customColor: customColorProp = "#000000",
  withLogo = true,
  prefix,
  logoId,
}: UseURLEntryProps) => {
  const [qrOptionsState, setQrOptionsState] = useState<QROptions>({
    withLogo: !!withLogo,
    ...(logoId ? { selectedLogoId: logoId } : {}),
  });

  const setQrOptions = (patch: Partial<QROptions>) =>
    setQrOptionsState((prev) => ({ ...prev, ...patch }));

  const [customColor, setCustomColor] = useState<string>(customColorProp);
  const [editedName, setEditedName] = useState("");
  const [editedUrl, setEditedUrl] = useState("");
  const [editedExpirationDate, setEditedExpirationDate] = useState("");
  const [editVisible, setEditVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded ?? false);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const [initialQrOptions, setInitialQrOptions] = useState<QROptions>({
    withLogo: !!withLogo,
    ...(logoId ? { selectedLogoId: logoId } : {}),
  });
  const [initialCustomColor, setInitialCustomColor] = useState(customColorProp);
  const [editedPrefix, setEditedPrefix] = useState(prefix);
  const [initialPrefix, setInitialPrefix] = useState(prefix);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useClickOutside(
    dropdownRef,
    () => setDownloadMenuOpen(false),
    downloadMenuOpen
  );
  useClickOutside(menuRef, () => setMenuOpen(false), menuOpen);

  const openEditModal = () => {
    setMenuOpen(false);
    setEditedName(name);
    setEditedUrl(url);
    setEditedExpirationDate(
      expirationDate ? new Date(expirationDate).toISOString().slice(0, 10) : ""
    );
    setInitialQrOptions(qrOptionsState);
    setInitialCustomColor(customColor);
    setEditedPrefix(prefix);
    setInitialPrefix(prefix);
    setEditVisible(true);
  };

  const closeEditModal = () => setEditVisible(false);

  return {
    qrOptions: qrOptionsState,
    setQrOptions,
    customColor,
    setCustomColor,
    editedName,
    setEditedName,
    editedUrl,
    setEditedUrl,
    editedExpirationDate,
    setEditedExpirationDate,
    editVisible,
    setEditVisible,
    isExpanded,
    setIsExpanded,
    downloadMenuOpen,
    setDownloadMenuOpen,
    menuOpen,
    setMenuOpen,
    initialQrOptions,
    initialCustomColor,
    dropdownRef,
    menuRef,
    openEditModal,
    closeEditModal,
    editedPrefix,
    setEditedPrefix,
    initialPrefix,
    logoId,
  };
};
